import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getUpcomingEvents, type UpcomingEvent } from '../lib/advisorApi';
import { formatDate, formatDaysAway, humanize } from '../lib/format';
import './advisor.css';

const HORIZONS = [30, 60, 90, 180] as const;

export function EventsScreen() {
  const { api } = useAuth();
  const [days, setDays] = useState<number>(30);
  const [events, setEvents] = useState<UpcomingEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await getUpcomingEvents(api, days);
        if (!cancelled) setEvents(res.events);
      } catch {
        if (!cancelled) setError('We could not load upcoming deliveries.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, days]);

  return (
    <div className="la-rise">
      <header className="page__head">
        <p className="la-label">The next {days} days</p>
        <h1 className="page__title">Upcoming</h1>
        <p className="page__sub">
          Messages your clients scheduled that are about to reach someone. A graduation, a
          twenty-first, a wedding — moments worth a call from you beforehand.
        </p>
      </header>

      <div className="toolbar">
        {HORIZONS.map((h) => (
          <button
            key={h}
            type="button"
            className="la-btn-secondary"
            aria-pressed={days === h}
            onClick={() => setDays(h)}
          >
            {h} days
          </button>
        ))}
      </div>

      {error !== null && (
        <p className="auth__alert" role="alert">
          {error}
        </p>
      )}

      {events !== null && events.length === 0 && (
        <div className="la-card page__empty">
          <p>Nothing is due in this window.</p>
        </div>
      )}

      {events !== null && events.length > 0 && (
        <div className="la-card tableWrap">
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Occasion</th>
                <th>Date</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.triggerId}>
                  <td className="table__name">
                    <Link className="table__link" to={`/clients/${e.vaultId}`}>
                      {e.clientName}
                    </Link>
                  </td>
                  <td>{humanize(e.lifeEventType ?? e.triggerType)}</td>
                  <td>{formatDate(e.eventDate)}</td>
                  <td>
                    <span className={`pill${e.daysAway < 7 ? ' pill--gold' : ''}`}>
                      {formatDaysAway(e.daysAway)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="la-card privacyNote">
        Deliveries tied to a death — of the client, a spouse, or a co-creator — are deliberately
        excluded from this list. Those are handled through the guardian process, not an advisor
        calendar.
      </p>
    </div>
  );
}
